import type { FmpFormStep } from '@strata-monorepo/find-my-plan/types/form'

export interface StepVariant {
  question: FmpFormStep
  requiredFields: string[]
  questionNames: string[]
}

export interface StepFamily {
  resolve: () => StepVariant
  stepNames: Set<string>
}

/**
 * Factory for multi-variation form steps.
 *
 * Conditions are evaluated in order — first match wins.
 * Falls back to `fallback` when no condition matches.
 *
 * Usage:
 *   const buildMyStepFamily = createStepFamily(
 *     [
 *       {
 *         condition: () => getParam('x') === 'a',
 *         question: variantA,
 *         requiredFields: [...],
 *         questionNames: [...]
 *       },
 *       {
 *         condition: () => getParam('x') === 'b',
 *         question: variantB,
 *         requiredFields: [...],
 *         questionNames: [...]
 *       },
 *     ],
 *     {
 *       question: defaultVariant,
 *       requiredFields: [...],
 *       questionNames: [...]
 *     }
 *   )
 *
 *   export const myStepFamily = () => buildMyStepFamily()
 *
 * Consumers:
 *   - myStepFamily().resolve()    → use in questionnaire flow configs
 *   - myStepFamily().stepNames    → use in QuestionStepBody to dispatch the right component
 */
export const createStepFamily = (
  variants: Array<{ condition: () => boolean } & StepVariant>,
  fallback: StepVariant,
): StepFamily => {
  return {
    resolve: () => variants.find((variant) => variant.condition()) ?? fallback,
    stepNames: new Set([fallback, ...variants].map((variant) => variant.question.name)),
  }
}
